import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { Box } from '@mui/material';

import { useContextMenuItemsWithAccess } from 'src/components/common/context_menu/context_menu_items_with_access';
import { InfoCard } from 'src/components/common/infocard/InfoCard';
import { ConfirmationModal } from 'src/components/common/modal/ConfirmationModal';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { IInfoColumn } from 'src/components/common/types/infocardTypes';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import { AnnotationTemplateTable } from 'src/features/annotation_template/components/AnnotationTemplateTable';
import { PatientTable } from 'src/features/patient/components/PatientTable';
import { QuestionnaireTemplateTable } from 'src/features/questionnaire_template/components/QuestionnaireTemplateTable';
import { CreateEditSiteModal } from 'src/features/site/components/CreateEditSiteModal';
import {
	getSiteDetails,
	SITE_LOADING_KEY,
	deleteSite,
} from 'src/features/site/store/siteThunks';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

const SiteHomePage: React.FC<{
	tableType?: 'patient' | 'questionnaire_template' | 'annotation_template';
}> = ({ tableType = 'patient' }) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);

	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const handleFetchSiteDetails = async () => {
		if (siteSlug) {
			await dispatch(
				getSiteDetails({
					siteSlug,
				}),
			);
		}
	};

	const handleDeleteSite = async () => {
		if (!siteSlug) return;
		dispatch(
			deleteSite({
				siteSlug: siteSlug,
				onSuccess: () => {
					snackbar('Site deleted successfully', 'success');
					setIsDeleteModalOpen(false);
					navigate('/dashboard', { replace: true });
				},
			}),
		);
	};

	useEffect(() => {
		handleFetchSiteDetails();
	}, [siteSlug]);

	const breadcrumbs = [{ label: siteDetails?.name || 'Site Details' }];

	const infoColumns: IInfoColumn[] = [
		{ label: 'Site Code', value: siteDetails?.code || '-' },
		{ label: 'Description', value: siteDetails?.description || '-' },
		{
			label: 'Total Patients',
			value: siteDetails?.total_patients?.toString() || '-',
		},
		{
			label: 'Total Studies',
			value: siteDetails?.total_studies?.toString() || '-',
		},
		{
			label: 'Created At',
			value: siteDetails?.created_at
				? formatTimestamp(siteDetails.created_at)
				: '-',
		},
	];

	const parsedContextMenuItems = useContextMenuItemsWithAccess([
		{
			label: 'Edit',
			icon: <ModeEditOutlinedIcon />,
			onClick: () => setIsEditModalOpen(true),
			allowedSiteRoles: ['super_admin'],
		},
		{
			label: 'Delete',
			icon: <DeleteOutlinedIcon color='error' />,
			onClick: () => setIsDeleteModalOpen(true),
			color: 'error',
			allowedSiteRoles: ['super_admin'],
		},
	]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box sx={{ p: 0.5 }}>
				<InfoCard
					title={siteDetails?.name || 'Loading...'}
					columns={infoColumns}
					loadingKey={SITE_LOADING_KEY}
					contextMenuItems={
						tableType === 'questionnaire_template' ||
						tableType === 'annotation_template'
							? []
							: parsedContextMenuItems
					}
				/>

				{siteDetails && (
					<CreateEditSiteModal
						open={isEditModalOpen}
						onClose={() => setIsEditModalOpen(false)}
						onSubmit={() => {
							setIsEditModalOpen(false);
							handleFetchSiteDetails();
						}}
						initialSiteData={siteDetails}
					/>
				)}

				<ConfirmationModal
					title='Are you sure you want to delete this site?'
					modalContent={`This action cannot be undone. All data associated with this site will also be deleted.`}
					modalOpen={isDeleteModalOpen}
					handleYes={handleDeleteSite}
					loadingKey={SITE_LOADING_KEY}
					handleNo={() => setIsDeleteModalOpen(false)}
				/>

				{tableType === 'patient' ? (
					<PatientTable siteSlug={siteSlug || ''} />
				) : tableType === 'annotation_template' ? (
					<AnnotationTemplateTable siteSlug={siteSlug || ''} />
				) : (
					<QuestionnaireTemplateTable siteSlug={siteSlug || ''} />
				)}
			</Box>
		</PageWithLayout>
	);
};

export default SiteHomePage;
